// src/oura/newscan/singleVenue.ts
//
// One place known by several area names (Jack, 17 Oct): a staycation villa in
// Bukit Tinggi that customers also ask about as Janda Baik or Bentong. It is
// one location, not three outlets, so the other names ride on the one outlet
// as `alsoSearchedAs`. Location questions are asked under every name and every
// answer counts toward the one place.
//
// Chains (cafes, clinics, gyms) keep Outlets: each outlet is a real place with
// its own town. Single-venue businesses (villas, venues, resorts, homestays)
// get Location: the row says so, there is no flagship star, and adding a second
// place is behind "Has more than one location?".
//
// Behind OURA_AREA_NAMES_ENABLED: the backend has to keep `alsoSearchedAs` and
// draft the location questions under each name before staff can rely on it.

import type { ScanDraftBusiness, ScanDraftOutlet } from "@service/aeo/scanDraftTypes";
import { OURA_AREA_NAMES_ENABLED } from "@oura/newscan/flags";

/** Categories that are one place by nature. Matched as words, so "Accomodation" (the site's spelling) counts too. */
const SINGLE_VENUE = /\b(villa|venue|resort|homestay|home stay|accomm?odation|chalet|retreat|glamping|campsite|event space|event hall|banquet|guesthouse|guest house|lodge|farmstay)s?\b/i;

export function isSingleVenueCategory(category: string): boolean {
    return SINGLE_VENUE.test(category);
}

/** The outlet's other area names: trimmed, each once (ignoring case), never its own name. Absent reads as none. */
export function otherAreaNames(o: ScanDraftOutlet | undefined): string[] {
    if (!o) return [];
    const seen = new Set([o.name.trim().toLowerCase()]);
    return (o.alsoSearchedAs ?? []).flatMap((raw) => {
        const name = raw.trim();
        const key = name.toLowerCase();
        if (!name || seen.has(key)) return [];
        seen.add(key);
        return [name];
    });
}

/** The outlet with its other area names cleaned the same way (after a rename, or names from the API). */
export function withCleanAreaNames(o: ScanDraftOutlet): ScanDraftOutlet {
    return o.alsoSearchedAs ? { ...o, alsoSearchedAs: otherAreaNames(o) } : o;
}

/** Every name the one place is asked about, its own first. */
export function areaNames(o: ScanDraftOutlet): string[] {
    return [o.name.trim(), ...otherAreaNames(o)].filter(Boolean);
}

/**
 * The row reads Location: one place (or none yet) at a business that is visited, and either
 * its category is a single venue or staff already gave the place other area names.
 */
export function isLocationMode(b: ScanDraftBusiness): boolean {
    if (!OURA_AREA_NAMES_ENABLED || b.buy !== "visit" || b.outlets.length > 1) return false;
    return isSingleVenueCategory(b.category) || otherAreaNames(b.outlets[0]).length > 0;
}

/**
 * Places staff added as outlets that look like other names for the first one: a single-venue
 * category, and every extra place is staff's own and in the first place's town. Null when
 * the list does not look like that (a place the site names is a real second venue).
 */
export function sameVenueCandidates(b: ScanDraftBusiness): ScanDraftOutlet[] | null {
    if (!OURA_AREA_NAMES_ENABLED || b.buy !== "visit" || b.outlets.length < 2 || !isSingleVenueCategory(b.category)) return null;
    const [first, ...rest] = b.outlets;
    const town = first.town.trim().toLowerCase();
    // No town yet is no evidence: the prompt waits until every place has one.
    if (!town) return null;
    const alike = rest.every((o) => o.source !== "site" && o.town.trim().toLowerCase() === town);
    return alike ? rest : null;
}

/** The outlet without its other area names (they belong to Visit us only). */
export function withoutAreaNames(o: ScanDraftOutlet): ScanDraftOutlet {
    const rest = { ...o };
    delete rest.alsoSearchedAs;
    return rest;
}

/** "Same place": the extra places become other names of the first one. */
export function mergeIntoFirst(outlets: ScanDraftOutlet[]): ScanDraftOutlet[] {
    const [first, ...rest] = outlets;
    if (!first) return outlets;
    const names = [...otherAreaNames(first), ...rest.map((o) => o.name.trim())];
    const own = first.name.trim().toLowerCase();
    const kept = names.filter((n, i) => n && n.toLowerCase() !== own && names.findIndex((x) => x.toLowerCase() === n.toLowerCase()) === i);
    return [{ ...first, alsoSearchedAs: kept }];
}
